import { useMemo } from "react";

import { PlatformUsage } from "components/Posts/PostForm";
import { FIELDS } from "constants/PostFormFields";
import { CHARACTER_LIMITS } from "constants/CharacterLimits";
import { filterAccountsByPlatformType } from "utils/accounts";
import {
  getTwitterLongformCapability,
  TWITTER_SHORT_LIMIT,
  TWITTER_LONGFORM_LIMIT,
} from "@/entities/accounts";
import {
  getPostCaptionCharacterCount,
  getTwitterPaidAccountWarning,
} from "../postFormValidator";
import CaptionField from "./CaptionField";
import { PlatformSpec } from "../../../../constants/Platforms";
import {
  AccountPlatformType,
  AccountType,
  PostLinkedInAccountType,
} from "../../../../shared/types/accounts";
import {
  FormValidationErrors,
  FormValidationWarnings,
} from "../../../../shared/types/post";
import { SkedEditorHistory } from "../../../../ui/SkedCaption/v2/sked-caption-v2";
import type { Label } from "../../../../entities/posts/components/templates/post-form-base-template";

const EMPTY_ERRORS: FormValidationErrors = {};
const EMPTY_WARNINGS: FormValidationWarnings = {};

const PostCaption = ({
  platforms,
  selectedPlatform,
  post,
  accounts,
  onChange,
  errors = EMPTY_ERRORS,
  warnings = EMPTY_WARNINGS,
  className = "",
  disabled = false,
  hashtagCount,
  hasMoreThanOnelinkedInAccount,
  selectedLinkedInAccountType,
  selectedLinkedUrn,
  formId,
  captionEditHistory,
  selectedContentLabel,
  accountGroupId,
  labelIds,
}: {
  platforms: PlatformSpec[];
  selectedPlatform: AccountPlatformType | "LINKED";
  post: { caption?: string; redirectUrl?: string };
  // Accounts targeted by the post — sourced from the same set the validator
  // inspects (`post.accounts`). Passed in so the per-account TW length cap
  // shown here stays in lockstep with the validator's warn/error decision.
  accounts?: AccountType[];
  onChange: (value: string) => void;
  errors?: FormValidationErrors;
  warnings?: FormValidationWarnings;
  className?: string;
  disabled?: boolean;
  hashtagCount: number;
  hasMoreThanOnelinkedInAccount?: boolean;
  selectedLinkedInAccountType?: PostLinkedInAccountType;
  selectedLinkedUrn?: string;
  formId?: string;
  captionEditHistory?: SkedEditorHistory;
  selectedContentLabel?: Label[];
  accountGroupId?: string;
  labelIds?: string[];
}) => {
  const caption = useMemo(() => post.caption || "", [post]);
  const hasYT = platforms.some((platform) => platform.TYPE === "YT");
  const hasPI = platforms.some((platform) => platform.TYPE === "PI");
  // Use the smallest known TW limit across selected accounts. Unknown plans
  // stay optimistic at 25,000 unless a confirmed non-Premium account is present.
  const twLimit = useMemo(() => {
    const twAccounts = (accounts || []).filter(
      filterAccountsByPlatformType("TW")
    );
    const anyNotCapable = twAccounts.some(
      (account) => getTwitterLongformCapability(account) === "not-capable"
    );
    return anyNotCapable ? TWITTER_SHORT_LIMIT : TWITTER_LONGFORM_LIMIT;
  }, [accounts]);

  // Get the lowest character limit from postPlatformConfig
  const limits = platforms.map((p) =>
    p.TYPE === "TW" ? twLimit : CHARACTER_LIMITS[p.TYPE] || CHARACTER_LIMITS.FB
  );
  const length = Math.min(...limits);
  const characterCount = useMemo(() => {
    return getPostCaptionCharacterCount(
      post,
      platforms,
      selectedPlatform,
      accounts
    );
  }, [accounts, platforms, post, selectedPlatform]);
  const captionWarnings = useMemo(() => {
    const shouldShowTwitterWarning =
      selectedPlatform === "TW" ||
      (selectedPlatform === "LINKED" &&
        platforms.some((platform) => platform.TYPE === "TW"));
    const twitterWarning = shouldShowTwitterWarning
      ? getTwitterPaidAccountWarning(post, accounts)
      : null;

    if (!twitterWarning) {
      return warnings;
    }

    const existingWarnings = (warnings[FIELDS.CAPTION]?.warnings ||
      []) as string[];

    if (existingWarnings.includes(twitterWarning)) {
      return warnings;
    }

    return {
      ...warnings,
      [FIELDS.CAPTION]: {
        warnings: [...existingWarnings, twitterWarning],
      },
    };
  }, [accounts, platforms, post, selectedPlatform, warnings]);

  return (
    <CaptionField
      label={
        <>
          <div className="mr-2">Caption</div>
          {hasYT &&
            ["LINKED", "YT"].includes(selectedPlatform) &&
            " (YouTube video description)"}
          {hasPI &&
            ["LINKED", "PI"].includes(selectedPlatform) &&
            " (Pin description)"}
          <PlatformUsage
            platforms={platforms}
            field={FIELDS.CAPTION}
            selectedPlatform={selectedPlatform}
          />
        </>
      }
      hashtagCount={hashtagCount}
      className={className}
      value={caption}
      characterCount={characterCount}
      disabled={disabled}
      onChange={onChange}
      maxLength={length}
      errors={errors}
      warnings={captionWarnings}
      selectedPlatform={selectedPlatform}
      platforms={platforms}
      hasMoreThanOnelinkedInAccount={hasMoreThanOnelinkedInAccount}
      selectedLinkedInAccountType={selectedLinkedInAccountType}
      selectedLinkedUrn={selectedLinkedUrn}
      formId={formId}
      captionEditHistory={captionEditHistory}
      selectedContentLabel={selectedContentLabel}
      accountGroupId={accountGroupId}
      labelIds={labelIds}
    />
  );
};

export default PostCaption;
